import type { CronSchedule } from "@mewbleh/nori-persistence";

type CronFields = {
  minute: Set<number>;
  hour: Set<number>;
  dayOfMonth: Set<number>;
  month: Set<number>;
  dayOfWeek: Set<number>;
  anyDayOfMonth: boolean;
  anyDayOfWeek: boolean;
};

function parseField(source: string, minimum: number, maximum: number, sunday = false): Set<number> {
  const values = new Set<number>();
  for (const item of source.split(",")) {
    const [rangeText, stepText] = item.split("/");
    if (!rangeText || (stepText !== undefined && !/^\d+$/u.test(stepText))) throw new Error(`Invalid cron field: ${source}`);
    const step = stepText === undefined ? 1 : Number(stepText);
    if (step < 1) throw new Error(`Invalid cron step: ${source}`);
    let start: number;
    let end: number;
    if (rangeText === "*") {
      start = minimum;
      end = maximum;
    } else if (rangeText.includes("-")) {
      const [startText, endText] = rangeText.split("-");
      if (!/^\d+$/u.test(startText ?? "") || !/^\d+$/u.test(endText ?? "")) throw new Error(`Invalid cron range: ${source}`);
      start = Number(startText);
      end = Number(endText);
    } else {
      if (!/^\d+$/u.test(rangeText)) throw new Error(`Invalid cron value: ${source}`);
      start = Number(rangeText);
      end = start;
    }
    if (start < minimum || start > maximum || end < minimum || end > maximum || end < start) throw new Error(`Cron value outside ${minimum}-${maximum}: ${source}`);
    for (let value = start; value <= end; value += step) values.add(sunday && value === 7 ? 0 : value);
  }
  if (!values.size) throw new Error(`Empty cron field: ${source}`);
  return values;
}

export function parseCronExpression(expression: string): CronFields {
  const fields = expression.trim().split(/\s+/u);
  if (fields.length !== 5) throw new Error("Cron expressions must have five fields: minute hour day-of-month month day-of-week");
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields as [string, string, string, string, string];
  return {
    minute: parseField(minute, 0, 59),
    hour: parseField(hour, 0, 23),
    dayOfMonth: parseField(dayOfMonth, 1, 31),
    month: parseField(month, 1, 12),
    dayOfWeek: parseField(dayOfWeek, 0, 7, true),
    anyDayOfMonth: dayOfMonth === "*",
    anyDayOfWeek: dayOfWeek === "*",
  };
}

function cronMatches(fields: CronFields, date: Date): boolean {
  const dayOfMonth = fields.dayOfMonth.has(date.getUTCDate());
  const dayOfWeek = fields.dayOfWeek.has(date.getUTCDay());
  const dayMatches = fields.anyDayOfMonth
    ? dayOfWeek
    : fields.anyDayOfWeek
      ? dayOfMonth
      : dayOfMonth || dayOfWeek;
  return fields.minute.has(date.getUTCMinutes())
    && fields.hour.has(date.getUTCHours())
    && fields.month.has(date.getUTCMonth() + 1)
    && dayMatches;
}

export function validateSchedule(schedule: CronSchedule): CronSchedule {
  if (schedule.kind === "at") {
    if (!Number.isSafeInteger(schedule.atMs) || schedule.atMs <= 0) throw new Error("atMs must be a positive Unix timestamp in milliseconds");
    return schedule;
  }
  if (schedule.kind === "every") {
    if (!Number.isSafeInteger(schedule.everyMs) || schedule.everyMs < 1_000) throw new Error("everyMs must be at least 1000 milliseconds");
    return schedule;
  }
  if (schedule.tz !== undefined && schedule.tz !== "UTC") throw new Error("Nori cron schedules currently support UTC only");
  parseCronExpression(schedule.expr);
  return { ...schedule, tz: "UTC" };
}

export function nextScheduleTime(schedule: CronSchedule, fromMs = Date.now()): number | undefined {
  validateSchedule(schedule);
  if (schedule.kind === "at") return schedule.atMs > fromMs ? schedule.atMs : undefined;
  if (schedule.kind === "every") return fromMs + schedule.everyMs;
  const fields = parseCronExpression(schedule.expr);
  const cursor = new Date(fromMs);
  cursor.setUTCSeconds(0, 0);
  cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
  const maximum = cursor.getTime() + 366 * 24 * 60 * 60_000;
  while (cursor.getTime() <= maximum) {
    if (cronMatches(fields, cursor)) return cursor.getTime();
    cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
  }
  throw new Error("Cron expression has no occurrence within the next 366 days");
}
